import { create } from 'zustand';

export type ToastTone = 'default' | 'good' | 'bad';
export interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
}

interface UiState {
  theme: 'light' | 'dark';
  navOpen: boolean;
  /** desktop only: the sidebar shrinks to icons */
  navCollapsed: boolean;
  toasts: Toast[];
  setNavOpen: (open: boolean) => void;
  toggleNavCollapsed: () => void;
  toggleTheme: () => void;
  toast: (message: string, tone?: ToastTone) => void;
  dismiss: (id: number) => void;
}

const systemTheme = () => (window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
const initialTheme = (): 'light' | 'dark' => {
  const attr = document.documentElement.getAttribute('data-theme');
  return attr === 'dark' || attr === 'light' ? attr : systemTheme();
};

const initialCollapsed = () => {
  try {
    const saved = localStorage.getItem('fms-nav-collapsed');
    // no saved choice: a narrow desktop window (tablet portrait) starts with the icon-only sidebar
    return saved === null ? window.innerWidth < 820 : saved === '1';
  } catch {
    return false;
  }
};

let nextId = 1;

/** Zustand holds UI preferences and toasts; server state lives in TanStack Query (PRD 5.6). */
export const useUi = create<UiState>((set, get) => ({
  theme: initialTheme(),
  navOpen: false,
  navCollapsed: initialCollapsed(),
  toasts: [],
  setNavOpen: (navOpen) => set({ navOpen }),
  toggleNavCollapsed: () => {
    const navCollapsed = !get().navCollapsed;
    try {
      localStorage.setItem('fms-nav-collapsed', navCollapsed ? '1' : '0');
    } catch {
      /* private mode */
    }
    set({ navCollapsed });
  },
  toggleTheme: () => {
    const theme = get().theme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('fms-theme', theme);
    } catch {
      /* private mode */
    }
    set({ theme });
  },
  toast: (message, tone = 'default') => {
    const id = nextId++;
    set((s) => ({ toasts: [...s.toasts, { id, message, tone }] }));
    setTimeout(() => get().dismiss(id), 3800);
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

export const toast = (message: string, tone?: ToastTone) => useUi.getState().toast(message, tone);
